import { useTranslation } from "react-i18next";

import { RecurrenceMode } from "../../flatbuffers/generated/archive/recurrence-mode";
import { daysOfWeekToBooleans, daysOfMonthToDays, hasLastDay } from "./scheduledActionBitmask";
import { TimedActionDraft } from "./scheduledActionTypes";
import { parseFloorSpec, describeFloors } from "./floorSpec";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function secondsToHHMM(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function daysOfWeekLabel(mask: number): string {
  const bools = daysOfWeekToBooleans(mask);
  const names = DAY_NAMES.filter((_, i) => bools[i]);
  return names.length === 0 ? "every day" : names.join(", ");
}

/**
 * Render a start–stop time window, calling out the overnight wraparound
 * explicitly when `stopNextDay` is set — "19:00–05:00 (→ next day)" rather
 * than a bare "19:00–05:00" that reads as same-day and would have an
 * operator wondering why the stop appears to fire before the start.
 */
function timeWindowLabel(
  t: (key: string, fallback: string) => string,
  startTime: string,
  stopTime: string,
  stopNextDay: boolean,
): string {
  const window = `${startTime}–${stopTime}`;
  if (!stopNextDay) return window;

  return `${window} (${t("scheduledActions.nextDay", "→ next day")})`;
}

const ORDINAL_WORDS = ["1st", "2nd", "3rd", "4th", "5th"] as const;

/**
 * "1st"/"2nd"/.../"last" for the MONTHLY_WEEKDAY summary. `last` overrides
 * `ordinal`, matching how the form and the server both treat the pair — see
 * `validateMonthlyWeekdayTiming` and ScheduledActionModeFields' Occurrence
 * select.
 */
function ordinalLabel(
  ordinal: number,
  last: boolean,
  t: (key: string, fallback: string) => string,
): string {
  if (last) return t("scheduledActions.last", "last");
  return t(`scheduledActions.ordinal.${ordinal}`, ORDINAL_WORDS[ordinal - 1] ?? `${ordinal}th`);
}

/**
 * The single weekday name a MONTHLY_WEEKDAY row's `days_of_week` mask carries.
 * Unlike `daysOfWeekLabel` (DAILY/WEEKLY, which treats mask 0 as "every day"),
 * an unset mask here has no honest reading — the row's own validation never
 * allows it — so it renders as "not set" rather than implying a day it does
 * not have.
 */
function singleDayOfWeekLabel(mask: number, t: (key: string, fallback: string) => string): string {
  const bools = daysOfWeekToBooleans(mask);
  const idx = bools.findIndex(Boolean);
  if (idx < 0) return t("scheduledActions.notSet", "not set");
  const label = DAY_NAMES[idx];
  return t(`scheduledActions.${label.toLowerCase()}`, label);
}

function daysOfMonthLabel(mask: number): string {
  const days = daysOfMonthToDays(mask);
  const parts: string[] = days.map(String);
  if (hasLastDay(mask)) {
    parts.push("last day");
  }
  return parts.length === 0 ? "no days" : parts.join(", ");
}

/**
 * Render an action's TARGET set — which banks, which cars, which floors — as a
 * short phrase for the list view.
 *
 * Kept separate from the schedule summary rather than folded into it: the
 * schedule answers "when" and this answers "where", and the existing summary
 * strings are asserted verbatim by tests and read by operators who are used to
 * their shape.
 *
 * Returns an EMPTY string for an action that targets a single bank, no specific
 * car and no floor range — the pre-existing shape, which the list already
 * describes elsewhere and which would only be noise to restate.
 */
export function describeTargets(draft: TimedActionDraft): string {
  const parts: string[] = [];

  const bankCount = draft.bank_ids?.length ?? 0;
  if (bankCount > 1) parts.push(`${bankCount} banks`);

  const liftCount = draft.lift_ids?.length ?? 0;
  if (liftCount > 1) {
    parts.push(`${liftCount} lifts`);
  } else if (draft.lift_id !== undefined && draft.lift_id !== "") {
    parts.push("1 lift");
  }

  const spec = draft.floor_spec ?? "";
  if (spec !== "") {
    const parsed = parseFloorSpec(spec);
    // A stored spec should always parse — the model hook rejects anything else —
    // but showing the raw text beats showing nothing if one ever slips through.
    parts.push(parsed.ok ? `floors ${describeFloors(parsed.floors)}` : `floors ${spec}`);
  }

  return parts.join(", ");
}

/**
 * Returns a function that renders a human-readable summary string for any
 * TimedActionDraft, covering all 5 RecurrenceMode variants.
 *
 * Uses useTranslation so it re-renders on language change. The function
 * itself is stable (depends only on t + draft values at call time).
 */
export function useScheduleSummary(): (draft: TimedActionDraft) => string {
  const [t] = useTranslation("global");

  return (draft: TimedActionDraft): string => {
    // Hoist shared time/tz values used by DAILY / WEEKLY / MONTHLY
    const startTime = secondsToHHMM(draft.start_seconds_of_day);
    const stopTime = secondsToHHMM(draft.stop_seconds_of_day);
    const tz = draft.timezone || "UTC";

    switch (draft.recurrence_mode) {
      case RecurrenceMode.ONE_SHOT: {
        const start = draft.start_at ?? t("scheduledActions.notSet", "not set");
        const stop = draft.stop_at ?? t("scheduledActions.notSet", "not set");
        return `${t("scheduledActions.once", "Once")}, ${start} → ${stop}`;
      }

      case RecurrenceMode.DAILY: {
        const days = daysOfWeekLabel(draft.days_of_week);
        const window = timeWindowLabel(t, startTime, stopTime, draft.stop_next_day);
        return `${t("scheduledActions.daily", "Daily")} ${window} ${t(
          "scheduledActions.on",
          "on",
        )} ${days} (${tz})`;
      }

      case RecurrenceMode.WEEKLY: {
        const days = daysOfWeekLabel(draft.days_of_week);
        const interval = draft.week_interval ?? 1;
        const window = timeWindowLabel(t, startTime, stopTime, draft.stop_next_day);
        const everyN =
          interval <= 1
            ? t("scheduledActions.everyWeek", "Every week")
            : `${t("scheduledActions.every", "Every")} ${interval} ${t(
                "scheduledActions.weeks",
                "weeks",
              )}`;
        return `${everyN} ${t("scheduledActions.on", "on")} ${days} ${window} (${tz})`;
      }

      case RecurrenceMode.MONTHLY: {
        const daysList = daysOfMonthLabel(draft.days_of_month ?? 0);
        const window = timeWindowLabel(t, startTime, stopTime, draft.stop_next_day);
        return `${t("scheduledActions.monthly", "Monthly")} ${t(
          "scheduledActions.on",
          "on",
        )} ${daysList} ${window} (${tz})`;
      }

      case RecurrenceMode.MONTHLY_WEEKDAY: {
        const day = singleDayOfWeekLabel(draft.days_of_week, t);
        const ordinal = ordinalLabel(draft.month_week_ordinal, draft.month_week_last, t);
        const window = timeWindowLabel(t, startTime, stopTime, draft.stop_next_day);
        return `${t("scheduledActions.monthly", "Monthly")} ${t(
          "scheduledActions.on",
          "on",
        )} ${t("scheduledActions.the", "the")} ${ordinal} ${day} ${window} (${tz})`;
      }

      case RecurrenceMode.INTERVAL: {
        // Recurring schedule reads as frequency + duration + start (not a
        // start→stop window, which implies a one-shot).
        const intervalMinutes = Math.round((draft.interval_seconds ?? 0) / 60);
        const minLabel = t("scheduledActions.minutes", "min");
        const every = `${t("scheduledActions.every", "Every")} ${intervalMinutes} ${minLabel}`;
        const start = draft.start_at ?? t("scheduledActions.notSet", "not set");
        if (draft.start_at && draft.stop_at) {
          const durationMin = Math.max(
            1,
            Math.round(
              (new Date(draft.stop_at).getTime() - new Date(draft.start_at).getTime()) / 60000,
            ),
          );
          return `${every} ${t("scheduledActions.for", "for")} ${durationMin} ${minLabel}, ${t(
            "scheduledActions.from",
            "from",
          )} ${draft.start_at}`;
        }
        return `${every}, ${t("scheduledActions.from", "from")} ${start}`;
      }

      default: {
        // exhaustiveness guard: TypeScript should never reach here
        const exhaustive: never = draft.recurrence_mode;
        return String(exhaustive);
      }
    }
  };
}

export default useScheduleSummary;
